<template>
  <v-app>
    <app-header 
      ref="appHeader"
      v-if="!hideHeader" 
      @scroll-to-section="scrollToSection"
      @open-cart="cartDrawer = true"
    />
    
    <v-main>
      <router-view @open-cart="cartDrawer = true"></router-view>
    </v-main>

    <!-- Cart Drawer -->
    <cart-drawer v-model="cartDrawer" @show-message="showSnackbar" />

    <!-- Login Dialog -->
    <v-dialog v-model="loginDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          Iniciar Sesión
          <v-spacer></v-spacer>
          <v-btn icon dark @click="loginDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="loginForm" v-model="loginValid">
            <v-alert
              v-if="showError"
              type="error"
              dismissible
              @input="showError = false"
              class="mb-4"
            >
              {{ errorMessage }}
            </v-alert>

            <v-text-field
              v-model="loginEmail"
              label="Correo Electrónico"
              :rules="emailRules"
              required
              prepend-icon="mdi-email"
            ></v-text-field>

            <v-text-field
              v-model="loginPassword"
              label="Contraseña"
              :rules="passwordRules"
              type="password"
              required
              prepend-icon="mdi-lock"
            ></v-text-field>

            <div class="d-flex align-center mb-4">
              <v-checkbox
                v-model="rememberMe"
                label="Recordarme"
              ></v-checkbox>
              <v-spacer></v-spacer>
              <a href="#" class="text-decoration-none" @click.prevent="showForgotPasswordDialog">¿Olvidaste tu contraseña?</a>
            </div>

            <v-btn
              color="primary"
              block
              large
              :disabled="!loginValid"
              @click="handleLogin"
            >
              Iniciar Sesión
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Signup Dialog -->
    <v-dialog v-model="signupDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          Crear Cuenta
          <v-spacer></v-spacer>
          <v-btn icon dark @click="signupDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="signupForm" v-model="signupValid">
            <v-alert
              v-if="showError"
              type="error"
              dismissible
              @input="showError = false"
              class="mb-4"
            >
              {{ errorMessage }}
            </v-alert>

            <v-text-field
              v-model="signupName"
              label="Nombre Completo"
              :rules="nameRules"
              required
              prepend-icon="mdi-account"
            ></v-text-field>

            <v-text-field
              v-model="signupEmail"
              label="Correo Electrónico"
              :rules="emailRules"
              required
              prepend-icon="mdi-email"
            ></v-text-field>

            <v-text-field
              v-model="signupPassword"
              label="Contraseña"
              :rules="passwordRules"
              type="password"
              required
              prepend-icon="mdi-lock"
            ></v-text-field>

            <v-text-field
              v-model="signupPasswordConfirm"
              label="Confirmar Contraseña"
              :rules="[...passwordRules, passwordMatchRule]"
              type="password"
              required
              prepend-icon="mdi-lock-check"
            ></v-text-field>

            <v-checkbox
              v-model="termsAccepted"
              :rules="[v => !!v || 'Debes aceptar los términos y condiciones']"
              required
              label="Acepto los términos y condiciones"
            ></v-checkbox>

            <v-btn
              color="primary"
              block
              large
              :disabled="!signupValid"
              @click="handleSignup"
            >
              Crear Cuenta
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Forgot Password Dialog -->
    <v-dialog v-model="forgotPasswordDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          Recuperar Contraseña
          <v-spacer></v-spacer>
          <v-btn icon dark @click="forgotPasswordDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="forgotPasswordForm" v-model="forgotPasswordValid">
            <v-alert
              v-if="showError"
              type="error"
              dismissible
              @input="showError = false"
              class="mb-4"
            >
              {{ errorMessage }}
            </v-alert>

            <v-alert
              v-if="showSuccess"
              type="success"
              dismissible
              @input="showSuccess = false"
              class="mb-4"
            >
              {{ successMessage }}
            </v-alert>

            <p class="mb-4">Ingresa tu correo electrónico y te enviaremos instrucciones para recuperar tu contraseña.</p>

            <v-text-field
              v-model="forgotPasswordEmail"
              label="Correo Electrónico"
              :rules="emailRules"
              required
              prepend-icon="mdi-email"
            ></v-text-field>

            <v-btn
              color="primary"
              block
              large
              :loading="loading"
              :disabled="!forgotPasswordValid || loading"
              @click="handleForgotPassword"
            >
              Enviar Instrucciones
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar.visible"
      :timeout="4000"
      color="success"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.visible = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>


  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AuthService from '@/services/auth'
import AppHeader from '@/components/AppHeader.vue'
import CartDrawer from '@/components/CartDrawer.vue'

export default {
  name: 'App',
  props: {
    resetToken: {
      type: String,
      default: null
    }
  },
  components: {
    AppHeader,
    CartDrawer
  },
  data: () => ({
    // Dialog controls
    loginDialog: false,
    signupDialog: false,
    forgotPasswordDialog: false,
    cartDrawer: false,

    // Login form
    loginValid: false,
    loginEmail: '',
    loginPassword: '',
    rememberMe: false,

    // Signup form
    signupValid: false,
    signupName: '',
    signupEmail: '',
    signupPassword: '',
    signupPasswordConfirm: '',
    termsAccepted: false,

    // Error handling
    errorMessage: '',
    showError: false,

    // Validation rules
    nameRules: [
      v => !!v || 'El nombre es requerido',
    ],
    emailRules: [
      v => !!v || 'El correo es requerido',
      v => /.+@.+\..+/.test(v) || 'El correo debe ser válido'
    ],
    passwordRules: [
      v => !!v || 'La contraseña es requerida',
      v => /(?=.*[A-Z])/.test(v) || 'Debe contener al menos una mayúscula',
      v => /(?=.*\d)/.test(v) || 'Debe contener al menos un número'
    ],

    // Forgot Password
    forgotPasswordValid: false,
    forgotPasswordEmail: '',

    // UI States
    loading: false,
    showSuccess: false,
    successMessage: '',
    snackbar: {
      visible: false,
      text: '',
    },

    // Drawer
    drawer: false
  }),
  computed: {
    ...mapGetters([
      'isAuthenticated',
      'currentUser'
    ]),
    passwordMatchRule() {
      return () => this.signupPassword === this.signupPasswordConfirm || 'Las contraseñas no coinciden'
    },
    hideHeader() {
      return this.$route.meta.hideHeader
    }
  },

  methods: {
    ...mapActions([
      'initializeAuth',
      'logout',
      'loadSubscriptionInfo'
    ]),
    scrollToSection(sectionId) {
      if (this.$route.path !== '/') {
        this.$router.push('/').then(() => {
          this.$nextTick(() => {
            this.performScroll(sectionId);
          });
        });
      } else {
        this.performScroll(sectionId);
      }
    },
    performScroll(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = this.$refs.appHeader?.$el?.clientHeight || 74;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
    async handleLogin() {
      if (this.$refs.loginForm.validate()) {
        this.loading = true;
        this.showError = false;
        try {
          const response = await this.$store.dispatch('login', {
            email: this.loginEmail,
            password: this.loginPassword
          });
          
          if (response.user) {
            this.loginDialog = false;
            this.$refs.loginForm.reset();
            
            // Si el usuario marcó "Recordarme", guardar en localStorage
            if (this.rememberMe) {
              localStorage.setItem('rememberMe', 'true');
              localStorage.setItem('emailForSignIn', this.loginEmail);
            } else {
              localStorage.removeItem('rememberMe');
              localStorage.removeItem('emailForSignIn');
            }

            this.$router.push('/dashboard/templates');
            this.showSnackbar('¡Bienvenido de nuevo!', 'success');
          } else {
            this.errorMessage = response.error || 'Credenciales inválidas.';
            this.showError = true;
          }
        } catch (error) {
          this.errorMessage = error.message || 'Ocurrió un error al iniciar sesión.';
          this.showError = true;
        } finally {
          this.loading = false;
        }
      }
    },
    async handleSignup() {
      if (this.$refs.signupForm.validate()) {
        this.loading = true;
        this.showError = false;
        try {
          const response = await this.$store.dispatch('signup', {
            name: this.signupName,
            email: this.signupEmail,
            password: this.signupPassword
          });

          if (response.user) {
            this.signupDialog = false;
            this.$refs.signupForm.reset();
            this.$router.push('/dashboard/templates');
            this.showSnackbar('¡Cuenta creada exitosamente!', 'success');
          } else {
            this.errorMessage = response.error || 'No se pudo crear la cuenta.';
            this.showError = true;
          }
        } catch (error) {
          this.errorMessage = error.message || 'Ocurrió un error durante el registro.';
          this.showError = true;
        } finally {
          this.loading = false;
        }
      }
    },
    async handleLogout() {
      await this.logout()
      this.$router.push('/')
    },
    showForgotPasswordDialog() {
      this.loginDialog = false;
      this.forgotPasswordDialog = true;
    },
    async handleForgotPassword() {
      if (this.$refs.forgotPasswordForm.validate()) {
        this.loading = true;
        try {
          const response = await AuthService.forgotPassword(this.forgotPasswordEmail);
          this.showSuccess = true;
          this.successMessage = response.message;
          setTimeout(() => {
            this.forgotPasswordDialog = false;
            this.resetForgotPasswordForm();
          }, 3000);
        } catch (error) {
          this.showError = true;
          this.errorMessage = error.message;
        } finally {
          this.loading = false;
        }
      }
    },
    async handleResetPassword() {
      // Removed - Reset password now uses ResetPasswordView.vue directly
    },
    resetForgotPasswordForm() {
      this.forgotPasswordEmail = '';
      if (this.$refs.forgotPasswordForm) {
        this.$refs.forgotPasswordForm.reset();
      }
      this.showError = false;
      this.showSuccess = false;
    },
    resetResetPasswordForm() {
      // Removed - Reset password now uses ResetPasswordView.vue directly
    },
    showLoginDialog() {
      this.signupDialog = false;
      this.loginDialog = true;
    },
    showSnackbar(text) {
      this.snackbar.text = text;
      this.snackbar.visible = true;
    },
    openLoginFromHeader() {
      if (this.$refs.appHeader) {
        this.$refs.appHeader.openLoginDialog()
      }
    }
  },
  async created() {
    // Reset password ahora se maneja en ResetPasswordView.vue
    // No necesitamos procesar el token aquí
    
    // Inicializar autenticación
    await this.initializeAuth()
    
    // Si está autenticado, cargar información de suscripción
    if (this.isAuthenticated) {
      try {
        await this.loadSubscriptionInfo()
      } catch (error) {
        console.error('Error loading subscription info on init:', error)
      }
    }

    this.$store.dispatch('cart/loadCart')

    this.$root.$on('open-login', this.openLoginFromHeader)
    this.$root.$on('open-cart', () => { this.cartDrawer = true })
    this.$root.$on('scroll-to-section', this.scrollToSection)
  },
  beforeDestroy() {
    this.$root.$off('open-login', this.openLoginFromHeader)
    this.$root.$off('open-cart')
    this.$root.$off('scroll-to-section', this.scrollToSection)
  }
};
</script>

<style>
.v-application {
  font-family: sans-serif !important;
}

html, body {
  overflow-x: hidden !important;
  max-width: 100vw !important;
}

.v-application--wrap {
  overflow-x: hidden !important;
  max-width: 100vw !important;
}

.v-main {
  overflow-x: hidden !important;
  max-width: 100vw !important;
}

.v-main__wrap {
  overflow-x: hidden !important;
}

@media (max-width: 600px) {
  .row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

.mobile-menu-sheet.v-sheet {
  background-color: #000000 !important;
}

.mobile-menu-list {
  background-color: transparent !important;
  background: transparent !important;
}

.mobile-menu-list .v-list-item:hover {
  background-color: #282828 !important;
}

.user-menu.v-list,
.user-menu.theme--light.v-list {
  background-color: #000000 !important;
}

.user-menu .v-list-item {
  color: white !important;
}

.user-menu .v-list-item:hover,
.user-menu .v-list-item:focus {
  background-color: #282828 !important;
}

.user-menu .v-list-item__title {
  color: white !important;
  font-size: 13px;
}

.user-menu .v-list-item__icon .v-icon {
  color: #C4C4C4 !important;
}
</style>
